import { api } from '../client';
import type { PageParams } from '@/types/api';
import type {
  Campus,
  CampusStatus,
  CampusSummary,
  School,
  SchoolStatus,
  SchoolSummary,
  UUID,
} from '@/types/domain';

/** Body of every PATCH /{id}/status endpoint. */
export interface StatusChangePayload {
  status: string;
  reason?: string;
}

export interface AddressPayload {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

/** Create carries the code; update does not, because the code is immutable. */
export interface CreateSchoolPayload {
  code: string;
  name: string;
  shortName?: string;
  legalName?: string;
  registrationNo?: string;
  email?: string;
  phone?: string;
  website?: string;
  logoUrl?: string;
  establishedDate?: string;
  address?: AddressPayload;
  timezone?: string;
  locale?: string;
  status?: SchoolStatus;
}

export type UpdateSchoolPayload = Omit<CreateSchoolPayload, 'code' | 'status'>;

export interface CreateCampusPayload {
  code: string;
  name: string;
  email?: string;
  phone?: string;
  principalName?: string;
  address?: AddressPayload;
  latitude?: number;
  longitude?: number;
  primary: boolean;
  status?: CampusStatus;
}

export type UpdateCampusPayload = Omit<CreateCampusPayload, 'code' | 'status'>;

export const schoolsApi = {
  list: (params: PageParams & { status?: SchoolStatus }) => api.getPage<School>('/schools', { params }),
  picker: () => api.get<SchoolSummary[]>('/schools/picker'),
  get: (id: UUID) => api.get<School>(`/schools/${id}`),
  create: (payload: CreateSchoolPayload) => api.post<School>('/schools', payload),
  update: (id: UUID, payload: UpdateSchoolPayload) => api.put<School>(`/schools/${id}`, payload),
  changeStatus: (id: UUID, payload: StatusChangePayload) =>
    api.patch<School>(`/schools/${id}/status`, payload),
  remove: (id: UUID) => api.delete(`/schools/${id}`),
};

export const campusesApi = {
  list: (params: PageParams & { schoolId?: UUID; status?: CampusStatus }) =>
    api.getPage<Campus>('/campuses', { params }),
  picker: () => api.get<CampusSummary[]>('/campuses/picker'),
  get: (id: UUID) => api.get<Campus>(`/campuses/${id}`),
  create: (payload: CreateCampusPayload) => api.post<Campus>('/campuses', payload),
  update: (id: UUID, payload: UpdateCampusPayload) => api.put<Campus>(`/campuses/${id}`, payload),
  changeStatus: (id: UUID, payload: StatusChangePayload) =>
    api.patch<Campus>(`/campuses/${id}/status`, payload),
  remove: (id: UUID) => api.delete(`/campuses/${id}`),
};
